<template>
  <!-- Cabeçalho do Progresso Geral -->
  <v-row class="mb-6">
    <v-col cols="12">
      <v-card flat border class="pa-6">
        <div class="d-flex align-center mb-4">
          <v-icon size="22" class="me-3">
            mdi-target
          </v-icon>
          <h2 class="text-subtitle-1 font-weight-medium">
            Atividades Concluídas - Geral
          </h2>
        </div>

        <v-row class="mb-2" align="center">
          <v-col cols="12" md="9" class="d-flex flex-column justify-center">
            <div class="mb-2 text-caption text-medium-emphasis">
              Progresso Total
            </div>
            <v-progress-linear :model-value="progressData.totalProgress" height="15" color="primary" bg-color="grey"
              rounded />
          </v-col>

          <v-col cols="12" md="3" class="d-flex justify-center justify-md-end align-center">
            <div class="text-h4 font-weight-bold text-primary">
              {{ progressData.totalProgress }}%
            </div>
          </v-col>
        </v-row>

        <v-row class="text-caption text-medium-emphasis">
          <v-col cols="12" md="6" class="d-flex align-center justify-center justify-md-start">
            {{ progressData.completedSubtasks }} de {{ progressData.totalSubtasks }} atividades concluídas
          </v-col>

          <v-col cols="12" md="6" class="d-flex justify-center justify-md-end align-center">
            {{ progressData.totalActivities }} atividades no projeto
          </v-col>
        </v-row>
      </v-card>
    </v-col>
  </v-row>

  <!-- Cards de Estatísticas -->
  <v-row class="mb-6">
    <v-col v-for="(stat, index) in statisticsCards" :key="index" cols="12" sm="6" md="3">
      <v-card flat border class="pa-4" height="120">
        <div class="d-flex align-center fill-height">

          <div style="width: 60px; height: 60px;" class="d-flex align-center justify-center me-4">
            <v-chip label :color="stat.iconColor" variant="tonal" style="width: 80%; height: 80%;"
              class="d-flex align-center justify-center">
              <v-icon :color="stat.iconColor" size="28">
                {{ stat.icon }}
              </v-icon>
            </v-chip>
          </div>

          <div class="d-flex flex-column justify-center">
            <div class="text-body-2 text-medium-emphasis mb-1">
              {{ stat.title }}
            </div>
            <div class="text-h4 font-weight-bold" :class="stat.valueClass">
              {{ stat.value }}
            </div>
          </div>

        </div>
      </v-card>
    </v-col>
  </v-row>

  <!-- Seção Inferior -->
  <v-row>
    <!-- Equipe -->
    <v-col cols="12" md="4">
      <v-card flat border class="pa-4 h-100">

        <div class="d-flex align-center mb-4">
          <v-icon size="22" class="me-3">
            mdi-account-multiple-outline
          </v-icon>
          <h2 class="text-subtitle-1 font-weight-medium">
            Equipe
          </h2>
        </div>

        <div class="d-flex justify-space-between align-center mb-2">
          <v-chip size="small" variant="flat" style="background-color: white; color: black;">
            Total de Usuários
          </v-chip>
          <span class="text-subtitle-2 font-weight-medium">{{ teamData.totalUsers }}</span>
        </div>

        <div class="d-flex justify-space-between align-center">
          <v-chip size="small" variant="flat" style="background-color: white; color: black;">
            Usuários Ativos
          </v-chip>
          <span class="text-subtitle-2 font-weight-medium">{{ teamData.activeUsers }}</span>
        </div>
      </v-card>
    </v-col>

    <!-- Status das Atividades -->
    <v-col cols="12" md="4">
      <v-card flat border class="pa-4 h-100">

        <div class="d-flex align-center mb-4">
          <v-icon size="22" class="me-3">
            mdi-calendar-check
          </v-icon>
          <h2 class="text-subtitle-1 font-weight-medium">
            Status das Atividades
          </h2>
        </div>

        <v-row v-for="(status, index) in activityStatus" :key="index" class="mb-2 align-center" no-gutters>
          <v-col cols="auto">
            <v-chip :color="status.color" variant="tonal" size="small">
              {{ status.label }}
            </v-chip>
          </v-col>

          <v-spacer />

          <v-col cols="auto">
            <span class="text-subtitle-2 font-weight-medium">
              {{ status.count }}
            </span>
          </v-col>
        </v-row>
      </v-card>
    </v-col>

    <!-- Alertas -->
    <v-col cols="12" md="4">
      <v-card flat border class="pa-4 h-100">

        <div class="d-flex align-center mb-4">
          <v-icon size="22" class="me-3">
            mdi-alert-circle-outline
          </v-icon>
          <h2 class="text-subtitle-1 font-weight-medium">
            Alertas
          </h2>
        </div>

        <v-chip :color="alertsData.atrasadas > 0 ? 'error' : 'success'" variant="tonal"
          class="text-subtitle-2 font-weight-medium">

          <v-icon :color="alertsData.atrasadas > 0 ? 'error' : 'success'" class="mr-2" size="18">
            {{ alertsData.atrasadas > 0 ? 'mdi-alert-circle-outline' : 'mdi-check-circle-outline' }}
          </v-icon>

          {{ alertsData.message }}
        </v-chip>
      </v-card>
    </v-col>

  </v-row>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import api from '@/services/api'

const loading = ref(false)
const dashboardData = ref({
  total_atividades: 0,
  concluidas: 0,
  em_andamento: 0,
  nao_iniciadas: 0,
  atrasadas: 0,
  equipe: {
    total_usuarios: 0,
    total_ativos: 0
  }
})

const progressData = computed(() => {
  const total = parseInt(dashboardData.value.total_atividades) || 1
  const concluidas = parseInt(dashboardData.value.concluidas) || 0
  const totalProgress = Math.round((concluidas / total) * 100)

  return {
    totalProgress,
    completedSubtasks: concluidas,
    totalSubtasks: total,
    totalActivities: total
  }
})

const teamData = computed(() => ({
  totalUsers: parseInt(dashboardData.value.equipe?.total_usuarios) || 0,
  activeUsers: parseInt(dashboardData.value.equipe?.total_ativos) || 0
}))

const alertsData = computed(() => {
  const atrasadas = parseInt(dashboardData.value.atrasadas) || 0
  return {
    message: atrasadas > 0 ? `${atrasadas} atividade(s) atrasada(s)` : 'Nenhuma atividade atrasada', atrasadas: atrasadas
  }
})

const statisticsCards = computed(() => [
  {
    title: 'Total de Atividades',
    value: parseInt(dashboardData.value.total_atividades) || 0,
    icon: 'mdi-chart-line-variant',
    iconColor: 'primary',
    valueClass: 'text-primary',
    cardClass: 'border-l-4 border-primary'
  },
  {
    title: 'Concluídas',
    value: parseInt(dashboardData.value.concluidas) || 0,
    icon: 'mdi-checkbox-marked-circle-outline',
    iconColor: 'success',
    valueClass: 'text-success',
    cardClass: 'border-l-4 border-success'
  },
  {
    title: 'Em Andamento',
    value: parseInt(dashboardData.value.em_andamento) || 0,
    icon: 'mdi-clock-outline',
    iconColor: 'warning',
    valueClass: 'text-warning',
    cardClass: 'border-l-4 border-warning'
  },
  {
    title: 'Atrasadas',
    value: parseInt(dashboardData.value.atrasadas) || 0,
    icon: 'mdi-alert-outline',
    iconColor: 'error',
    valueClass: 'text-error',
    cardClass: 'border-l-4 border-error'
  }
])

const activityStatus = computed(() => [
  {
    label: 'Não Iniciadas',
    count: parseInt(dashboardData.value.nao_iniciadas) || 0,
    color: 'blue-grey'
  },
  {
    label: 'Em Andamento',
    count: parseInt(dashboardData.value.em_andamento) || 0,
    color: 'warning'
  },
  {
    label: 'Concluídas',
    count: parseInt(dashboardData.value.concluidas) || 0,
    color: 'success'
  }
])

const fetchProjectData = async () => {
  loading.value = true

  try {
    const response = await api.get(`atividades/dashboard/${localStorage.getItem('rn573_user_id')}`)

    if (response.data.status === 200 && response.data.data) {
      dashboardData.value = response.data.data
    } else {
      throw new Error(response.data.message || 'Erro ao carregar dados do dashboard')
    }
  } catch (error) {
    console.error('Erro ao carregar dados do projeto:', error)

  } finally {
    loading.value = false
  }
}

const refreshData = () => {
  fetchProjectData()
}

onMounted(() => {
  fetchProjectData()
})

defineExpose({
  refreshData
})
</script>
